import { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import config from "./config";

/**
 * Protected posts — a community post whose button hides a private link (a Zoom
 * room, a workshop doc) that only members of that group can open.
 *
 * Shared by both feed renderers (MembershipShell and CommunityFeed), which is
 * the whole point of the module: the rule about what may be shown and what must
 * be asked for lives in ONE place. The URL is never in a feed payload — the
 * click asks the API, and the API re-checks the member's access to the group
 * every time (MembershipFeedController::communityPostProtectedOpen).
 *
 * Creating one is an admin/instructor capability (`can.protected_posts`); the
 * server re-checks that too, so the fields here are a convenience, not a gate.
 */

export const PROTECTED_TEXT_MAX = 48;

/**
 * A full http(s) link with a real domain. The API validates it again; this is
 * so the Post button can refuse before the round trip. Any other scheme is out:
 * the app opens this in a new tab, where `mailto:` is useless and `javascript:`
 * is worse.
 */
export function isHttpUrl(value) {
  const v = String(value || "").trim();
  if (!v) return false;
  try {
    const u = new URL(v);
    return (u.protocol === "http:" || u.protocol === "https:") && u.hostname.includes(".");
  } catch {
    return false;
  }
}

/** "" when the composer's protected fields are good to post. */
export function protectedError({ on, text, url, expiry }) {
  if (!on) return "";
  if (!String(text || "").trim()) return "Add the text for the button.";
  if (!isHttpUrl(url)) return "Add a full link, starting with http:// or https://";
  if (expiry && new Date(expiry).getTime() <= Date.now()) return "The expiry has to be in the future.";
  return "";
}

/**
 * <input type="datetime-local"> gives a WALL CLOCK with no zone ("2026-09-18T15:37").
 * The server runs in UTC, so sending that string verbatim made "3:37 pm" mean
 * 3:37 pm UTC — five and a half hours of extra life for an IST creator's link.
 * Convert to a real instant here; the server stores and compares UTC.
 */
function expiryToUtc(local) {
  const v = String(local || "").trim();
  if (!v) return "";
  const d = new Date(v); // parsed in the browser's zone, which is what was typed
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}

/**
 * The reverse, for prefilling the edit form: a UTC instant from the API back
 * into the local wall clock the input expects.
 */
export function expiryToLocalInput(utc) {
  const v = String(utc || "").trim();
  if (!v) return "";
  // Accept both shapes the API has sent: ISO with a zone, and the older
  // "Y-m-d H:i:s" (always UTC), which Safari will not parse without the T/Z.
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(v) ? v : `${v.replace(" ", "T")}Z`);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The fields the composer sends — {} when the post carries no protected link. */
export function protectedFields({ on, text, url, expiry }) {
  if (!on) return {};
  return {
    protected_post: 1,
    protected_button_text: String(text || "").trim(),
    protected_url: String(url || "").trim(),
    protected_expires_at: expiryToUtc(expiry),
  };
}

/**
 * The checkbox + its three fields, for the Create Post modal. Render it only
 * when the member may create protected posts AND the composer is on a plain
 * text/image post — the poll, video and audio branches of the API return long
 * before the protected one, so the link would be silently dropped there.
 *
 * `inputCls` / `focusProps` come from the host file so the fields look like the
 * rest of that composer.
 */
export function ProtectedFields({ state, set, primary, inputCls, focusProps = {} }) {
  const { on, text, url, expiry } = state;
  const error = protectedError(state);

  return (
    <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50/60 p-3.5">
      <label className="flex cursor-pointer items-start gap-3">
        {/* The native box is kept for keyboard and screen readers, but drawn by
            the span below it: a bare checkbox renders as a white square on this
            app's dark theme, where "ticked" and "not ticked" look the same. */}
        <input
          type="checkbox"
          checked={on}
          onChange={(e) => (e.target.checked ? set({ on: true }) : set({ on: false, text: "", url: "", expiry: "" }))}
          className="peer sr-only"
        />
        <span
          aria-hidden="true"
          className={`mt-0.5 grid h-[18px] w-[18px] shrink-0 place-items-center rounded-[5px] border-2 transition peer-focus-visible:ring-2 peer-focus-visible:ring-offset-1 ${
            on ? "border-transparent" : "border-gray-300 bg-transparent"
          }`}
          style={on ? { backgroundColor: primary } : undefined}
        >
          {on && (
            <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3.4">
              <path strokeLinecap="round" strokeLinejoin="round" d="m5 12.5 5 5 9-11" />
            </svg>
          )}
        </span>
        <span className="min-w-0">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
            <LockIcon className="h-4 w-4 shrink-0" style={{ color: primary }} />
            Protected link
          </span>
          <span className="mt-0.5 block text-[12.5px] leading-relaxed text-gray-500">
            Members see a button, not the URL — good for a Zoom room or a private resource.
          </span>
        </span>
      </label>

      {on && (
        <div className="mt-3.5 space-y-3">
          <div>
            <label className="mb-1.5 flex items-baseline justify-between gap-3 text-[12px] font-bold uppercase tracking-[0.06em] text-gray-500">
              Button text
              <span className={`text-[11px] font-semibold tabular-nums ${text.length >= PROTECTED_TEXT_MAX ? "text-amber-600" : "text-gray-400"}`}>
                {text.length}/{PROTECTED_TEXT_MAX}
              </span>
            </label>
            <input
              value={text}
              maxLength={PROTECTED_TEXT_MAX}
              onChange={(e) => set({ text: e.target.value })}
              placeholder="Join Live Session"
              className={inputCls}
              {...focusProps}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-[12px] font-bold uppercase tracking-[0.06em] text-gray-500">
              Protected URL
            </label>
            <input
              value={url}
              onChange={(e) => set({ url: e.target.value })}
              placeholder="https://zoom.us/j/…"
              className={inputCls}
              {...focusProps}
            />
            <p className="mt-1.5 text-[12px] text-gray-400">This link is never shown inside the post.</p>
          </div>
          <div>
            <label className="mb-1.5 block text-[12px] font-bold uppercase tracking-[0.06em] text-gray-500">
              Expiry date / time{" "}
              <span className="font-semibold normal-case tracking-normal text-gray-400">· optional</span>
            </label>
            <input
              type="datetime-local"
              value={expiry}
              onChange={(e) => set({ expiry: e.target.value })}
              className={inputCls}
              {...focusProps}
            />
            <p className="mt-1.5 text-[12px] text-gray-400">After this, the button stops opening the link.</p>
          </div>
          {error && <p className="text-[12px] font-medium text-red-600 dark:text-red-300">{error}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * The button on a protected post.
 *
 * It cannot be a plain <a href> — the payload has no URL in it. The tab is
 * opened BEFORE the request, because a popup blocker will not accept a
 * window.open that happens after an await; it is then pointed at the resolved
 * link, so this stays an ordinary "opens in a new tab" click.
 */
export function ProtectedLink({ post, token, member, primary, onRequireAuth }) {
  const link = post.protected_link || {};
  const [busy, setBusy] = useState(false);
  // The server's answer wins over the payload's: a feed rendered before the
  // expiry passed still says "not expired".
  const [expired, setExpired] = useState(!!link.expired);

  const open = async () => {
    if (busy) return;
    if (!member?.auth_token) {
      onRequireAuth?.("login");
      return;
    }
    setBusy(true);
    const tab = window.open("about:blank", "_blank");
    if (tab) tab.opener = null;
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-post-protected-open`, {
        community_token: token,
        member_token: member.auth_token,
        post_id: post.post_id,
      });
      if (res.data?.error === 0 && res.data?.url) {
        if (tab) tab.location.href = res.data.url;
        else window.location.href = res.data.url;
        return;
      }
      tab?.close();
      if (res.data?.expired) setExpired(true);
      toast.error(res.data?.msg || "This link is no longer available.");
    } catch (err) {
      tab?.close();
      if (err?.response?.data?.expired) setExpired(true);
      toast.error(err?.response?.data?.msg || "Could not open this link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={open}
        disabled={busy || expired}
        style={expired ? undefined : { backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
        className={`inline-flex max-w-full items-center gap-2 rounded-full px-5 py-2.5 text-[14px] font-bold transition active:scale-95 ${
          expired ? "cursor-not-allowed bg-gray-100 text-gray-400" : "hover:opacity-90 disabled:opacity-60"
        }`}
      >
        <LockIcon className="h-4 w-4 shrink-0" open={expired} />
        <span className="truncate">
          {busy ? "Opening…" : expired ? "Link expired" : link.button_text || "Open protected link"}
        </span>
        {!expired && (
          <svg className="h-3.5 w-3.5 shrink-0 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H18v4.5M18 6l-7 7M16.5 14.5V18a1.5 1.5 0 0 1-1.5 1.5H6A1.5 1.5 0 0 1 4.5 18V9A1.5 1.5 0 0 1 6 7.5h3.5" />
          </svg>
        )}
      </button>
      <p className="mt-1.5 text-[12.5px] text-gray-500">
        {expired ? "This link has expired." : "Protected link. Only active members can access this."}
      </p>
    </div>
  );
}

/** Closed padlock, or an open shackle once the link has expired. */
function LockIcon({ open = false, ...props }) {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9" {...props}>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
      <path strokeLinecap="round" d={open ? "M8 10.5V7.75a4 4 0 0 1 7.4-2.1" : "M8 10.5V7.75a4 4 0 1 1 8 0v2.75"} />
    </svg>
  );
}
